import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Activity, Droplets, Settings as Cog, ShieldCheck, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/hooks/usePoll";
import { useState, useEffect } from "react";
import { api, setCurrency } from "@/lib/api";

const LINKS = [
  { to: "/levels", label: "Levels", icon: Activity, testid: "nav-link-levels" },
  { to: "/oil-pairs", label: "Oil Pairs", icon: Droplets, testid: "nav-link-oil-pairs" },
  { to: "/settings", label: "Settings", icon: Cog, testid: "nav-link-settings" },
];

export default function Shell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  useNotifications(user);
  const [, setSym] = useState("£");
  useEffect(() => {
    const load = () => api.get("/broker/status").then((r) => setSym(setCurrency(r.data.account?.currency))).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const viewer = user?.role === "viewer";
  const guard = (e) => {
    if (!viewer) return;
    if (e.type === "keydown" && !["Enter", " "].includes(e.key) && !e.target.closest("input, textarea")) return;
    const el = e.target.closest("button, input, select, textarea, [role=switch], [role=checkbox]");
    if (!el || el.closest("[role=tab], [data-viewer-ok]")) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "click") toast("View only account. Changes are switched off.");
  };
  return (
    <div className="grain min-h-screen bg-transparent" onClickCapture={guard} onPointerDownCapture={guard} onKeyDownCapture={guard} onBeforeInputCapture={guard}>
      <header className="fixed inset-x-0 top-0 z-50 h-14 border-b border-white/[0.06] bg-[#07090E]/75 backdrop-blur-xl">
        <div className="mx-auto flex h-full max-w-[1500px] items-center justify-between gap-3 px-3 sm:px-6">
          <div className="flex items-center gap-2.5">
            <img data-testid="brand-logo" src="/logo192.png" alt="Trenex Systems logo" className="h-8 w-8 rounded-md ring-1 ring-white/10" />
            <div data-testid="brand-wordmark" className="wordmark hidden whitespace-nowrap font-display text-sm font-bold uppercase tracking-[0.2em] sm:block sm:text-lg sm:tracking-[0.32em]">
              Trenex Systems
            </div>
          </div>
          <nav className="flex items-center gap-1">
            {[...LINKS, ...(user?.role === "superadmin" ? [{ to: "/super", label: "Super admin", icon: ShieldCheck, testid: "nav-link-super" }] : [])].map(({ to, label, icon: I, testid }) => (
              <NavLink key={to} to={to} data-testid={testid} className={({ isActive }) => `flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm transition-colors ${isActive ? "border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#EBD7A0]" : "border border-transparent text-slate-400 hover:bg-white/[0.04] hover:text-slate-100"}`}>
                <I className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </NavLink>
            ))}
            <button data-viewer-ok data-testid="nav-signout-button" onClick={async () => { await logout(); nav("/login"); }} className="ml-1 flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-slate-400 transition-colors hover:text-rose-300">
              <LogOut className="h-4 w-4" />
              <span className="hidden md:inline">Sign out</span>
            </button>
          </nav>
        </div>
      </header>
      <main className="relative z-10 mx-auto max-w-[1500px] space-y-5 px-3 pb-16 pt-20 sm:px-6">
        {viewer && (
          <div data-testid="viewer-banner" className="flex items-center gap-2 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-4 py-2 text-sm text-[#EBD7A0]">
            <Eye className="h-4 w-4" /> Investor view. You are looking at the live dashboard in view only mode.
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
